/** TanStack Query hooks for admin background job management. */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type { AdminBackgroundJob, AdminJobListParams, PaginatedResponse } from '@formency/shared';

// Re-export for convenience
export type { AdminBackgroundJob, AdminJobListParams } from '@formency/shared';

// ─── Query key factory ──────────────────────────────────────────────────────

export const adminJobKeys = {
  all: ['f', 'admin-jobs'] as const,
  list: (params?: AdminJobListParams) => [...adminJobKeys.all, 'list', params] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/** Fetch paginated list of all background jobs (admin). */
export function useAdminJobs(params?: AdminJobListParams, options?: { refetchInterval?: number }) {
  return useQuery<PaginatedResponse<AdminBackgroundJob>>({
    queryKey: adminJobKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<AdminBackgroundJob>>(
        '/admin/jobs',
        params as Record<string, unknown> | undefined
      ),
    placeholderData: keepPreviousData,
    refetchInterval: options?.refetchInterval,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/** Cancel a background job (admin). */
export function useAdminCancelJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<{ jobId: string }>(`/admin/jobs/${jobId}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminJobKeys.all });
      toast.success('Job cancelled');
    },
    onError: () => {
      toast.error('Failed to cancel job');
    },
  });
}

/** Resume a failed or cancelled background job (admin). */
export function useAdminResumeJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<{ jobId: string }>(`/admin/jobs/${jobId}/resume`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminJobKeys.all });
      toast.success('Job resumed');
    },
    onError: () => {
      toast.error('Failed to resume job');
    },
  });
}
